import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { EASE, Reveal } from '../components/motion.tsx';
import { OfficeExport } from '../components/OfficeExport.tsx';
import { Avatar, Button, Callout, Chip, Dialog, Empty, Field, PageHead, Panel, Segmented, Skeleton, Tabs, useToast } from '../components/ui.tsx';
import { ApiError, get, post, qs } from '../lib/api.ts';
import { date, idr, idrCompact } from '../lib/format.ts';
import { useMe } from '../lib/session.ts';

interface Entry {
  id: string;
  kind: string;
  amount: string;
  interest: string;
  balanceAfter: string;
  occurredOn: string;
  notes: string | null;
}
interface Ledger {
  balance: string;
  entries: Entry[];
}
interface Member {
  id: string;
  name: string;
  employeeNo: string | null;
  status: string;
  balance: string;
}

const KIND_TONE: Record<string, 'ok' | 'info' | 'warn' | 'plain'> = { mandatory: 'ok', voluntary: 'info', interest: 'info', withdrawal: 'warn', adjustment: 'plain', opening: 'plain', reversal: 'warn' };

/** What the row added to the balance: the saving or withdrawal plus any interest it carried. */
const movement = (e: Entry) => Number(e.amount) + Number(e.interest);

export function Cooperative() {
  const me = useMe().data!;
  const desk = me.access.cooperative;
  const own = !!me.employee;
  const [tab, setTab] = useState<'mine' | 'desk'>(own ? 'mine' : 'desk');
  return (
    <div>
      <PageHead
        eyebrow="Koperasi karyawan"
        title={
          <>
            Your savings, <em>growing quietly.</em>
          </>
        }
        lede="Mandatory and voluntary savings are deducted from payroll and recorded here as an append-only ledger. Balances are never edited, only added to."
        aside={desk ? <OfficeExport label="Balances" title="Cooperative balances" note="Every current member of the office, and anyone who left with money still saved, with their balance today." href={(officeId) => `/api/coop/balances.xlsx${qs({ officeId })}`} /> : null}
      />
      {desk && own ? <Tabs idPrefix="coop" value={tab} onChange={setTab} tabs={[{ id: 'mine', label: 'My savings' }, { id: 'desk', label: 'Members' }]} /> : null}
      <div style={desk && own ? { marginTop: 28 } : undefined}>{tab === 'desk' && desk ? <Desk /> : <MySavings />}</div>
    </div>
  );
}

function MySavings() {
  const q = useQuery({ queryKey: ['coop'], queryFn: () => get<Ledger | null>('/api/coop/balance') });
  const bal = idrCompact(q.data?.balance);
  return (
    <Reveal className="grid-12">
      <Reveal.Item className="col-4">
        <div className="ledger" style={{ ['--cols' as string]: 1 }}>
          <div className="ledger__cell ledger__cell--accent">
            <div className="ledger__value">
              {bal.value}
              <small>{bal.unit}</small>
            </div>
            <div className="ledger__label eyebrow">Balance</div>
            <div className="ledger__delta num">{idr(q.data?.balance)}</div>
          </div>
        </div>
      </Reveal.Item>
      <Reveal.Item className="col-8">
        <Panel eyebrow="Ledger" title="Movements" action={q.data?.entries.length ? <LedgerExcel /> : undefined}>
          <Movements loading={q.isLoading} entries={q.data?.entries} />
        </Panel>
      </Reveal.Item>
    </Reveal>
  );
}

/** The last twelve months of movements, as legacy's per-member Excel. */
function LedgerExcel({ employeeId }: { employeeId?: string }) {
  return (
    <Button size="sm" variant="ghost" icon="download" title="The last twelve months of movements" onClick={() => window.location.assign(`/api/coop/ledger.xlsx${qs({ employeeId })}`)}>
      Excel
    </Button>
  );
}

function Movements({ loading, entries }: { loading: boolean; entries: Entry[] | undefined }) {
  if (loading) return <Skeleton h={200} />;
  if (!entries?.length) return <Empty title="No movements yet." />;
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Type</th>
          <th className="right">Amount</th>
          <th className="right">Balance</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id}>
            <td className="mono">{date(e.occurredOn)}</td>
            <td>
              <Chip tone={KIND_TONE[e.kind] ?? 'plain'}>{e.kind}</Chip>
              {e.kind === 'adjustment' && e.notes ? <div className="muted" style={{ fontSize: 'var(--step--1)', marginTop: 4 }}>{e.notes}</div> : null}
            </td>
            <td className="right num" style={{ color: movement(e) < 0 ? 'var(--clay)' : undefined }}>
              {idr(movement(e), { decimals: !Number.isInteger(movement(e)) })}
            </td>
            <td className="right num">{idr(e.balanceAfter)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------- Cooperative desk (hr.cooperative.*)
function Desk() {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState<'active' | 'resigned' | 'all'>('active');
  const [selected, setSelected] = useState<string | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 220);
    return () => clearTimeout(t);
  }, [q]);
  const list = useQuery({ queryKey: ['coop-members', debounced, status], queryFn: () => get<Member[]>(`/api/coop/members${qs({ q: debounced, status })}`), placeholderData: keepPreviousData });
  const member = list.data?.find((m) => m.id === selected) ?? list.data?.[0] ?? null;
  return (
    <Reveal className="grid-12">
      <Reveal.Item className="col-5 stack" style={{ alignContent: 'start' }}>
        <div className="search-field">
          <Icon name="search" width={16} height={16} />
          <input className="input" placeholder="Name or employee no." value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search members" />
        </div>
        <Segmented
          label="Employment"
          idPrefix="coop-status"
          value={status}
          onChange={setStatus}
          options={[
            { id: 'active', label: 'Active' },
            { id: 'resigned', label: 'Resigned' },
            { id: 'all', label: 'All' },
          ]}
        />
        <div className="panel" style={{ padding: 6 }}>
          {list.isLoading ? (
            <Skeleton h={320} />
          ) : !list.data?.length ? (
            <div style={{ padding: 20 }}>
              <p className="muted">{status === 'resigned' ? 'No one who has left matches.' : 'No one matches.'}</p>
            </div>
          ) : (
            <ul className="list-plain" role="listbox" aria-label="Members">
              {list.data.map((m, i) => (
                <motion.li key={m.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: EASE, delay: Math.min(i, 12) * 0.02 }}>
                  <button type="button" className="access-row" aria-selected={m.id === member?.id} onClick={() => setSelected(m.id)}>
                    <Avatar name={m.name} size={32} />
                    <span className="access-row__main">
                      <span className="access-row__name">{m.name}</span>
                      <span className="access-row__meta">{m.employeeNo ?? 'No number'}{m.status === 'active' ? '' : ` · ${m.status}`}</span>
                    </span>
                    <span className="access-row__side num">{idr(m.balance)}</span>
                  </button>
                </motion.li>
              ))}
            </ul>
          )}
        </div>
      </Reveal.Item>
      <Reveal.Item className="col-7">
        <AnimatePresence mode="wait">
          {member ? (
            <motion.div key={member.id} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.4, ease: EASE }}>
              <MemberLedger member={member} />
            </motion.div>
          ) : (
            <Empty title="Pick a member on the left." />
          )}
        </AnimatePresence>
      </Reveal.Item>
    </Reveal>
  );
}

function MemberLedger({ member }: { member: Member }) {
  const q = useQuery({ queryKey: ['coop', member.id], queryFn: () => get<Ledger>(`/api/coop/balance${qs({ employeeId: member.id })}`) });
  const [adjusting, setAdjusting] = useState(false);
  const balance = q.data?.balance ?? member.balance;
  return (
    <div className="stack">
      <div className="panel" style={{ padding: 26 }}>
        <div className="row" style={{ gap: 16, alignItems: 'flex-start' }}>
          <Avatar name={member.name} size={56} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="display" style={{ fontSize: '2rem', lineHeight: 1.05 }}>{member.name}</div>
            <div className="muted mono" style={{ fontSize: 12, marginTop: 4 }}>{member.employeeNo ?? 'No employee number'}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="eyebrow">Balance</div>
            <div className="num" style={{ fontSize: '1.4rem', marginTop: 4 }}>{idr(balance)}</div>
          </div>
        </div>
        <div className="row" style={{ marginTop: 18 }}>
          <Button size="sm" icon="plus" onClick={() => setAdjusting(true)}>
            Adjust balance
          </Button>
          <LedgerExcel employeeId={member.id} />
        </div>
      </div>
      <Panel eyebrow="Ledger" title="Movements">
        <Movements loading={q.isLoading} entries={q.data?.entries} />
      </Panel>
      {adjusting ? <AdjustDialog member={member} balance={balance} onClose={() => setAdjusting(false)} /> : null}
    </div>
  );
}

function AdjustDialog({ member, balance, onClose }: { member: Member; balance: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState<{ direction: 'add' | 'subtract'; amount: string; notes: string }>({ direction: 'add', amount: '', notes: '' });
  const amount = f.amount.replace(/[^\d]/g, '');
  const signed = amount ? `${f.direction === 'subtract' ? '-' : ''}${amount}` : '';
  const after = Number(balance) + Number(signed || 0);
  const ready = Number(amount) > 0 && f.notes.trim().length >= 5 && after >= 0;
  const m = useMutation({
    mutationFn: () => post('/api/coop/adjustments', { employeeId: member.id, amount: signed, notes: f.notes.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['coop'] });
      void qc.invalidateQueries({ queryKey: ['coop-members'] });
      toast({ title: 'Adjustment recorded', body: `${member.name}’s balance is now ${idr(after)}.` });
      onClose();
    },
  });
  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow={member.name}
      title="Adjust the balance"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={m.isPending} disabled={!ready} onClick={() => m.mutate()}>
            Record adjustment
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted" style={{ fontSize: 'var(--step--1)' }}>The ledger is never edited. This adds a new row; to undo it, record the opposite adjustment.</p>
        <Segmented label="Direction" idPrefix="coop-adjust" value={f.direction} onChange={(direction) => setF({ ...f, direction })} options={[{ id: 'add', label: 'Add' }, { id: 'subtract', label: 'Subtract' }]} />
        <Field label="Amount (Rp)" hint={signed ? `Balance after: ${idr(after)}` : `Balance now: ${idr(balance)}`} error={after < 0 ? 'That would take the balance below zero.' : null}>
          {(p) => <input {...p} className="input input--mono" inputMode="numeric" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />}
        </Field>
        <Field label="Reason" hint="Required. Kept on the ledger row and in the audit log.">
          {(p) => <textarea {...p} className="input" rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />}
        </Field>
        {m.error ? <Callout tone="bad">{m.error instanceof ApiError ? m.error.message : 'Failed'}</Callout> : null}
      </div>
    </Dialog>
  );
}
